import React, { useState, useMemo, useEffect } from "react";
import {
    FaEdit, FaPlusCircle, FaTrashAlt, FaSignOutAlt,
    FaUpload, FaSearch, FaBox, FaShoppingBag, FaTag,
    FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt,
    FaCalendarAlt, FaFileInvoice, FaImage, FaDownload,
    FaHashtag, FaPalette, FaRuler, FaClock, FaSortAmountDown,
    FaCheck, FaTruck, FaChartBar, FaFilter, FaFileAlt,
    FaListAlt, FaClipboardCheck, FaWhatsapp
} from "react-icons/fa";
import { ref, set, push, remove } from "firebase/database";
import { db } from "../../Pages/OurProduct/firebase";
import { myCustomTheme } from "../../Theme";
import { MASTER_COLOR_PALETTE } from "../../Pages/OurProduct/constants";
import { generateInvoicePDF, downloadDesignImage, generateSalesReportPDF } from "../../Pages/OurProduct/utils/orderUtils";
import "./style/AdminDashboard.css";

const MONTH_NAMES = ["All Months", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

/* ── Parse order date → { month, year } ────────────────────── */
const parseOrderDate = (dateStr) => {
    if (!dateStr) return { month: 0, year: 0 };
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return { month: d.getMonth() + 1, year: d.getFullYear() };
    const parts = dateStr.split(" ");
    if (parts.length === 3) {
        const m = MONTH_NAMES.indexOf(parts[1]);
        return { month: m > 0 ? m : 0, year: parseInt(parts[2]) || 0 };
    }
    return { month: 0, year: 0 };
};

/* ─── ACTION BUTTON — shared style ─────────────────────────── */
const ActionBtn = ({ onClick, bg, color, icon: Icon, label, disabled = false, minWidth = "130px" }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        style={{
            flex: 1, minWidth,
            padding: "11px 16px",
            borderRadius: "12px", border: "none",
            background: bg, color: color,
            fontWeight: "700", fontSize: "13px",
            cursor: disabled ? "wait" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: "7px", opacity: disabled ? 0.6 : 1,
            transition: "opacity .15s"
        }}
    >
        {Icon && <Icon />} {label}
    </button>
);

/* ─── ORDER DETAIL CARD ─────────────────────────────────────── */
const OrderCard = ({ order, onDelete, theme, actionMode = "pending" }) => {
    const [expanded, setExpanded] = useState(false);
    const [updating, setUpdating] = useState(false);

    const isBuyAll = Boolean(order.isBuyAll && order.items && order.items.length > 0);
    const orderItems = isBuyAll ? order.items : null;
    const status = (order.status || "pending").toLowerCase();

    const statusConfig = {
        pending: { label: "Pending", bg: "#fef3c7", color: "#92400e", Icon: FaClock },
        confirmed: { label: "Confirmed", bg: "#dcfce7", color: "#15803d", Icon: FaCheck },
        placed: { label: "Placed", bg: "#dbeafe", color: "#1d4ed8", Icon: FaTruck }
    };
    const sc = statusConfig[status] || statusConfig.pending;
    const StatusIcon = sc.Icon;

    const handleStatus = (e, newStatus) => {
        e.stopPropagation(); setUpdating(true);
        set(ref(db, `orders/${order.firebaseKey}/status`), newStatus).finally(() => setUpdating(false));
    };

    const buildWhatsappMessage = () => {
        const productLines = isBuyAll
            ? orderItems.map((item, idx) => `${idx + 1}. ${item.productName} — ₹${item.price}`).join("\n")
            : `${order.productName || "Product"}${order.quantity ? ` | Qty: ${order.quantity}` : ""}`;

        return `✅ *Order Confirmation*\n\nHello ${order.name || "Customer"},\n\nYour order has been received successfully.\n\n━━━━━━━━━━━━━━━━━━━━\n📦 *Order Details*\n━━━━━━━━━━━━━━━━━━━━\n💳 Order ID: ${order.orderId || "-"}\n🧾 Invoice No: ${order.invoiceNumber || "-"}\n📌 Product(s):\n${productLines}\n\n💰 Total Amount: ₹${order.totalAmount || 0}\n💳 Payment: Cash on Delivery\n🚚 Expected Delivery: ${order.deliveryDate || "7-9 Business Days"}${order.deliveryTime ? ` by ${order.deliveryTime}` : ""}\n\nThank you for choosing *Nevyati Engineering Consultancy*.\n\nRegards,\n*Nevyati Engineering Consultancy*`;
    };

    const buildCancelWhatsappMessage = () => {
        const productLines = isBuyAll
            ? orderItems.map((item, idx) => `${idx + 1}. ${item.productName} — ₹${item.price}`).join("\n")
            : `${order.productName || "Product"}${order.quantity ? ` | Qty: ${order.quantity}` : ""}`;

        return `❌ *Order Cancelled*\n\nHello ${order.name || "Customer"},\n\nYour order has been cancelled.\n\n━━━━━━━━━━━━━━━━━━━━\n📦 *Cancelled Order Details*\n━━━━━━━━━━━━━━━━━━━━\n🆔 Order ID: ${order.orderId || "-"}\n🧾 Invoice No: ${order.invoiceNumber || "-"}\n📌 Product(s):\n${productLines}\n\n💰 Total Amount: ₹${order.totalAmount || 0}\n\nFor any query, please contact our support team.\n\nRegards,\n*Nevyati Engineering Consultancy*`;
    };

    const cleanWhatsappNumber = (phone) => {
        const digits = String(phone || "").replace(/\D/g, "");
        if (!digits) return "";
        return digits.length === 10 ? `91${digits}` : digits;
    };

    const openCustomerWhatsapp = () => {
        const customerNumber = cleanWhatsappNumber(order.phone);

        if (!customerNumber) {
            alert("Customer WhatsApp number not found.");
            return;
        }

        const message = encodeURIComponent(buildWhatsappMessage());

        // Mobile WhatsApp App
        const mobileUrl = `whatsapp://send?phone=${customerNumber}&text=${message}`;

        // Fast WhatsApp Web
        const webUrl = `https://web.whatsapp.com/send?phone=${customerNumber}&text=${message}`;

        // Open blank tab instantly
        const newWindow = window.open("", "_blank");

        // Detect mobile
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

        if (newWindow) {
            newWindow.location.href = isMobile ? mobileUrl : webUrl;
        } else {
            // fallback
            window.location.href = isMobile ? mobileUrl : webUrl;
        }
    };

    const openCancelWhatsapp = () => {
        const customerNumber = cleanWhatsappNumber(order.phone);
        if (!customerNumber) {
            alert("Customer WhatsApp number not found.");
            return;
        }
        const message = encodeURIComponent(buildCancelWhatsappMessage());

        const mobileUrl = `whatsapp://send?phone=${customerNumber}&text=${message}`;

        // Fast WhatsApp Web
        const webUrl = `https://web.whatsapp.com/send?phone=${customerNumber}&text=${message}`;
        
        // Open blank tab instantly
        const newWindow = window.open("", "_blank");
        // Detect mobile
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

         if (newWindow) {
            newWindow.location.href = isMobile ? mobileUrl : webUrl;
        } else {
            // fallback
            window.location.href = isMobile ? mobileUrl : webUrl;
        }
    };

    const handleConfirmOrder = (e) => {
        e.stopPropagation();
        setUpdating(true);
        set(ref(db, `orders/${order.firebaseKey}/status`), "confirmed")
            .finally(() => setUpdating(false));
    };

    const handleInvoice = (e) => {
        e.stopPropagation();
        const pdfItems = isBuyAll
            ? orderItems.map(i => ({ productName: i.productName, productImage: i.productImage || "", type: i.type, price: i.price, hasCustomDesign: i.hasCustomDesign || false }))
            : null;
        generateInvoicePDF({
            orderId: order.orderId, invoiceNumber: order.invoiceNumber || "",
            name: order.name, email: order.email || "", phone: order.phone || "", address: order.address || "",
            productName: isBuyAll ? orderItems.map(i => i.productName).join(", ") : (order.productName || ""),
            colorName: order.colorName || "",
            selectedInch: order.isCustomized ? order.selectedInch || "6" : null,
            quantity: order.quantity || 1,
            productTotal: order.productTotal || order.totalAmount,
            shippingCharge: order.shippingCharge || 0,
            shippingZone: order.shippingZone || "Standard Delivery",
            totalAmount: order.totalAmount || 0,
            deliveryDate: order.deliveryDate || "", deliveryTime: order.deliveryTime || "6:00 PM",
            date: order.date || "", isCustomized: order.isCustomized || false,
            items: pdfItems
        });
    };

    const handleWhatsappMessage = (e) => {
        e.stopPropagation();
        openCustomerWhatsapp();
    };

    const handleCancelDelete = (e) => {
        e.stopPropagation();
        if (!window.confirm("Cancel and delete this order?")) return;
        openCancelWhatsapp();
        onDelete(order.firebaseKey);
    };

    const handleDesign = (img, id, e) => {
        e.stopPropagation();
        if (img && img !== "None") downloadDesignImage(img, id);
    };

    const pill = (color, bg, extra = {}) => ({
        display: "inline-flex", alignItems: "center", gap: "5px",
        background: bg, color, borderRadius: "8px",
        padding: "4px 10px", fontSize: "12px", fontWeight: "700", ...extra
    });

    const headerImage = isBuyAll ? (orderItems[0]?.productImage || "") : (order.productImage || "");
    const headerLabel = isBuyAll ? `${orderItems.length} items (Cart Order)` : (order.productName || "—");

    return (
        <div style={{ background: theme.colors.header_footer_bg, border: `1.5px solid ${expanded ? theme.colors.button_bg : theme.colors.high_light}`, borderRadius: "18px", overflow: "hidden", transition: "border-color .2s", marginBottom: "14px" }}>

            {/* COLLAPSED HEADER */}
            <div onClick={() => setExpanded(!expanded)} style={{ display: "flex", alignItems: "center", gap: "16px", padding: "16px 20px", cursor: "pointer", flexWrap: "wrap" }}>
                {headerImage
                    ? <img src={headerImage} alt="" style={{ width: "52px", height: "52px", objectFit: "cover", borderRadius: "10px", border: `1px solid ${theme.colors.high_light}`, flexShrink: 0 }} />
                    : <div style={{ width: "52px", height: "52px", borderRadius: "10px", background: theme.colors.bg_primary, display: "flex", alignItems: "center", justifyContent: "center", color: theme.colors.menu_text, fontSize: "20px", flexShrink: 0 }}><FaBox /></div>
                }
                <div style={{ flex: 1, minWidth: "130px" }}>
                    <p style={{ margin: 0, fontWeight: "800", fontSize: "15px", color: theme.colors.heading }}>{order.name}</p>
                    <p style={{ margin: "2px 0 0", fontSize: "11px", color: theme.colors.menu_text, fontFamily: "monospace" }}>{order.orderId}</p>
                </div>
                <div style={{ flex: 1, minWidth: "110px" }}>
                    <p style={{ margin: 0, fontSize: "13px", fontWeight: "600", color: theme.colors.heading }}>{headerLabel}</p>
                    <div style={{ display: "flex", gap: "5px", marginTop: "4px", flexWrap: "wrap" }}>
                        {isBuyAll && <span style={pill("#1d4ed8", "#eff6ff")}><FaShoppingBag /> Cart</span>}
                        {!isBuyAll && order.isCustomized && <span style={pill("#92400e", "#fef3c7")}>Customized</span>}
                        <span style={pill(sc.color, sc.bg, { fontSize: "11px" })}><StatusIcon /> {sc.label}</span>
                    </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <p style={{ margin: 0, fontWeight: "800", fontSize: "17px", color: "#16a34a" }}>Rs.{order.totalAmount}</p>
                    <p style={{ margin: "2px 0 0", fontSize: "11px", color: theme.colors.menu_text }}>{order.date}</p>
                </div>
                <span style={{ fontSize: "18px", color: theme.colors.menu_text, transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s", flexShrink: 0 }}>▾</span>
            </div>

            {/* EXPANDED PANEL */}
            {expanded && (
                <div style={{ padding: "0 20px 20px", borderTop: `1.5px solid ${theme.colors.high_light}` }}>

                    {/* Buy All items */}
                    {isBuyAll && (
                        <div style={{ marginTop: "18px", borderRadius: "12px", border: `1.5px solid ${theme.colors.high_light}`, overflow: "hidden" }}>
                            <div style={{ padding: "10px 16px", background: "#eff6ff", borderBottom: "1px solid #bfdbfe" }}>
                                <p style={{ margin: 0, fontWeight: "700", fontSize: "13px", color: "#1d4ed8" }}><FaShoppingBag style={{ marginRight: "6px" }} />Cart Order — {orderItems.length} Items</p>
                            </div>
                            {orderItems.map((item, idx) => (
                                <div key={idx} style={{ display: "flex", alignItems: "flex-start", gap: "14px", padding: "12px 16px", borderBottom: idx < orderItems.length - 1 ? `1px solid ${theme.colors.high_light}` : "none", background: theme.colors.header_footer_bg }}>
                                    <img src={item.productImage || ""} alt="" style={{ width: "56px", height: "56px", objectFit: "cover", borderRadius: "10px", border: `1.5px solid ${theme.colors.high_light}`, flexShrink: 0 }} />
                                    <div style={{ flex: 1 }}>
                                        <p style={{ margin: "0 0 2px", fontWeight: "700", fontSize: "14px", color: theme.colors.heading }}>{item.productName}</p>
                                        <p style={{ margin: "0 0 5px", fontSize: "12px", color: theme.colors.menu_text }}>{item.type}{item.type === "Customize" && " · 6\""}</p>
                                        {item.type === "Customize" && <span style={pill("#92400e", "#fef3c7")}>Customized</span>}
                                        {item.type === "Customize" && item.hasCustomDesign && item.productImage && (
                                            <div style={{ marginTop: "10px", padding: "12px", background: "#fefce8", border: "1.5px solid #fde68a", borderRadius: "10px", display: "flex", alignItems: "flex-start", gap: "12px" }}>
                                                <FaImage style={{ color: "#d97706", fontSize: "13px", marginTop: "2px", flexShrink: 0 }} />
                                                <div>
                                                    <p style={{ margin: "0 0 8px", fontSize: "11px", fontWeight: "700", color: "#92400e" }}>Customer's Custom Design</p>
                                                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                                                        <img src={item.productImage} alt="Design" style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "8px", border: "2px solid #fde68a", cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); window.open(item.productImage, "_blank"); }} />
                                                        <button onClick={(e) => handleDesign(item.productImage, order.orderId + "_" + idx, e)} style={{ padding: "7px 13px", borderRadius: "8px", border: "none", background: "#f59e0b", color: "#fff", fontWeight: "700", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "5px" }}><FaDownload /> Download</button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <span style={{ fontWeight: "800", fontSize: "15px", color: "#16a34a", whiteSpace: "nowrap" }}>Rs.{item.price}</span>
                                </div>
                            ))}
                            <div style={{ display: "flex", justifyContent: "space-between", padding: "11px 16px", background: "#f8fafc", borderTop: `1px solid ${theme.colors.high_light}` }}>
                                <span style={{ fontWeight: "700", fontSize: "13px", color: theme.colors.menu_text }}>Items Total (GST incl.)</span>
                                <span style={{ fontWeight: "800", fontSize: "15px", color: theme.colors.heading }}>Rs.{orderItems.reduce((s, i) => s + (i.price || 0), 0)}</span>
                            </div>
                        </div>
                    )}

                    {/* Info grid */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: "12px", marginTop: "18px" }}>
                        {order.invoiceNumber && (
                            <div style={{ background: "#eff6ff", borderRadius: "12px", padding: "13px 15px", border: "1px solid #bfdbfe" }}>
                                <p style={{ margin: "0 0 5px", fontSize: "10px", fontWeight: "700", color: "#1d4ed8", textTransform: "uppercase", letterSpacing: ".6px" }}><FaFileInvoice style={{ marginRight: "4px" }} /> Invoice No.</p>
                                <p style={{ margin: 0, fontWeight: "800", fontSize: "13px", color: "#1e40af", fontFamily: "monospace" }}>{order.invoiceNumber}</p>
                            </div>
                        )}
                        {[
                            { icon: FaHashtag, label: "Order ID", val: order.orderId, mono: true },
                            { icon: FaUser, label: "Customer", val: order.name },
                            { icon: FaPhone, label: "Phone", val: order.phone || "—" },
                            { icon: FaEnvelope, label: "Email", val: order.email || "—", wrap: true },
                        ].map(({ icon: Icon, label, val, mono, wrap }) => (
                            <div key={label} style={{ background: theme.colors.bg_primary, borderRadius: "12px", padding: "13px 15px", border: `1px solid ${theme.colors.high_light}` }}>
                                <p style={{ margin: "0 0 5px", fontSize: "10px", fontWeight: "700", color: theme.colors.menu_text, textTransform: "uppercase", letterSpacing: ".6px" }}><Icon style={{ marginRight: "4px" }} />{label}</p>
                                <p style={{ margin: 0, fontWeight: "700", fontSize: "13px", color: theme.colors.heading, fontFamily: mono ? "monospace" : undefined, wordBreak: wrap ? "break-all" : undefined }}>{val}</p>
                            </div>
                        ))}
                        <div style={{ background: theme.colors.bg_primary, borderRadius: "12px", padding: "13px 15px", border: `1px solid ${theme.colors.high_light}`, gridColumn: "span 2" }}>
                            <p style={{ margin: "0 0 5px", fontSize: "10px", fontWeight: "700", color: theme.colors.menu_text, textTransform: "uppercase", letterSpacing: ".6px" }}><FaMapMarkerAlt style={{ marginRight: "4px" }} />Address</p>
                            <p style={{ margin: 0, fontWeight: "600", fontSize: "13px", color: theme.colors.heading, lineHeight: "1.5" }}>{order.address || "—"}</p>
                        </div>
                        {!isBuyAll && (
                            <div style={{ background: theme.colors.bg_primary, borderRadius: "12px", padding: "13px 15px", border: `1px solid ${theme.colors.high_light}` }}>
                                <p style={{ margin: "0 0 5px", fontSize: "10px", fontWeight: "700", color: theme.colors.menu_text, textTransform: "uppercase", letterSpacing: ".6px" }}><FaBox style={{ marginRight: "4px" }} />Product</p>
                                <p style={{ margin: 0, fontWeight: "700", fontSize: "13px", color: theme.colors.heading }}>{order.productName}</p>
                                {order.colorName && <p style={{ margin: "3px 0 0", fontSize: "12px", color: theme.colors.menu_text, display: "flex", alignItems: "center", gap: "5px" }}><FaPalette />{order.colorName}</p>}
                                {order.isCustomized && order.selectedInch && <p style={{ margin: "3px 0 0", fontSize: "12px", color: theme.colors.menu_text, display: "flex", alignItems: "center", gap: "5px" }}><FaRuler />{order.selectedInch} inches</p>}
                            </div>
                        )}
                        <div style={{ background: theme.colors.bg_primary, borderRadius: "12px", padding: "13px 15px", border: `1px solid ${theme.colors.high_light}` }}>
                            <p style={{ margin: "0 0 5px", fontSize: "10px", fontWeight: "700", color: theme.colors.menu_text, textTransform: "uppercase", letterSpacing: ".6px" }}>Bill</p>
                            {!isBuyAll && <p style={{ margin: 0, fontSize: "13px", color: theme.colors.heading }}>Qty: <strong>{order.quantity}</strong></p>}
                            {order.shippingCharge !== undefined && <p style={{ margin: "3px 0 0", fontSize: "12px", color: theme.colors.menu_text }}>Shipping: <strong>{order.shippingCharge === 0 ? "FREE" : `Rs.${order.shippingCharge}`}</strong></p>}
                            <p style={{ margin: "4px 0 0", fontWeight: "800", fontSize: "16px", color: "#16a34a" }}>Rs.{order.totalAmount}</p>
                            <p style={{ margin: "2px 0 0", fontSize: "11px", color: "#d97706", fontWeight: "600" }}>Cash on Delivery</p>
                        </div>
                        <div style={{ background: "#f0fdf4", borderRadius: "12px", padding: "13px 15px", border: "1px solid #bbf7d0" }}>
                            <p style={{ margin: "0 0 5px", fontSize: "10px", fontWeight: "700", color: "#14532d", textTransform: "uppercase", letterSpacing: ".6px" }}><FaCalendarAlt style={{ marginRight: "4px" }} />Delivery</p>
                            <p style={{ margin: 0, fontWeight: "700", fontSize: "13px", color: "#15803d" }}>{order.deliveryDate || "—"}</p>
                            {order.deliveryTime && <p style={{ margin: "3px 0 0", fontSize: "12px", color: "#166534", display: "flex", alignItems: "center", gap: "4px" }}><FaClock style={{ fontSize: "11px" }} />by {order.deliveryTime}</p>}
                        </div>
                        <div style={{ background: theme.colors.bg_primary, borderRadius: "12px", padding: "13px 15px", border: `1px solid ${theme.colors.high_light}` }}>
                            <p style={{ margin: "0 0 5px", fontSize: "10px", fontWeight: "700", color: theme.colors.menu_text, textTransform: "uppercase", letterSpacing: ".6px" }}><FaClock style={{ marginRight: "4px" }} />Ordered On</p>
                            <p style={{ margin: 0, fontWeight: "700", fontSize: "13px", color: theme.colors.heading }}>{order.date || "—"}</p>
                            {order.orderTime && <p style={{ margin: "3px 0 0", fontSize: "12px", color: theme.colors.menu_text }}>at {order.orderTime}</p>}
                        </div>
                    </div>

                    {/* Single product custom design */}
                    {!isBuyAll && order.designImage && order.designImage !== "None" && (
                        <div style={{ marginTop: "16px", padding: "16px", background: "#fefce8", border: "1.5px solid #fde68a", borderRadius: "14px" }}>
                            <p style={{ margin: "0 0 12px", fontWeight: "700", fontSize: "13px", color: "#92400e", display: "flex", alignItems: "center", gap: "6px" }}><FaImage />Customer's Custom Design</p>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" }}>
                                <img src={order.designImage} alt="Design" style={{ width: "110px", height: "110px", objectFit: "cover", borderRadius: "10px", border: "2px solid #fde68a", cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); window.open(order.designImage, "_blank"); }} />
                                <button onClick={(e) => handleDesign(order.designImage, order.orderId, e)} style={{ padding: "10px 18px", borderRadius: "10px", border: "none", background: "#f59e0b", color: "#fff", fontWeight: "700", fontSize: "13px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", alignSelf: "flex-end" }}><FaDownload />Download Design</button>
                            </div>
                        </div>
                    )}

                    {/* STATUS + ACTIONS */}
                    <div style={{ marginTop: "18px" }}>
                        {/* Status bar */}
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px", padding: "11px 15px", background: "#f8fafc", borderRadius: "12px", border: `1px solid ${theme.colors.high_light}` }}>
                            <span style={{ fontSize: "11px", fontWeight: "700", color: theme.colors.menu_text, textTransform: "uppercase", letterSpacing: ".6px" }}>Status:</span>
                            <span style={pill(sc.color, sc.bg, { fontSize: "13px" })}><StatusIcon /> {sc.label}</span>
                        </div>

                        {/* Action buttons — controlled by order stage */}
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            {actionMode === "pending" && (
                                <>
                                    <ActionBtn onClick={handleInvoice} bg="#1e293b" color="#fff" icon={FaFileInvoice} label="Invoice PDF" />
                                    <ActionBtn onClick={handleConfirmOrder} bg="#16a34a" color="#fff" icon={FaCheck} label="Confirm" disabled={updating} minWidth="110px" />
                                    <ActionBtn onClick={handleCancelDelete} bg="#fee2e2" color="#ef4444" icon={FaTrashAlt} label="Delete" minWidth="90px" />
                                </>
                            )}
                            {actionMode === "confirmed" && (
                                <>
                                    <ActionBtn onClick={(e) => handleStatus(e, "placed")} bg="#1d4ed8" color="#fff" icon={FaTruck} label="Place Order" disabled={updating} minWidth="130px" />
                                    <ActionBtn onClick={handleInvoice} bg="#1e293b" color="#fff" icon={FaFileInvoice} label="Invoice PDF" />
                                </>
                            )}
                            {actionMode === "placed" && (
                                <>
                                    <ActionBtn onClick={handleWhatsappMessage} bg="#dcfce7" color="#15803d" icon={FaWhatsapp} label="WhatsApp Message" />
                                    <ActionBtn onClick={handleInvoice} bg="#1e293b" color="#fff" icon={FaFileInvoice} label="Invoice PDF" />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/* ─── MAIN ADMIN DASHBOARD ──────────────────────────────────── */
const AdminDashboard = ({
    currentAdminName, products, orders, categories,
    isEditMode, editProductId,
    newProdName, setNewProdName,
    newProdDescription, setNewProdDescription,
    newProdType, setNewProdType,
    newProdMrp, setNewProdMrp,
    newProdDiscount, setNewProdDiscount,
    newProdImagesList, setNewProdImagesList,
    newProdSelectedColors, setNewProdSelectedColors,
    newProdCategory, setNewProdCategory,
    adminSearchQuery, setAdminSearchQuery,
    cancelEditMode, startEditProductWorkflow,
    showToast, onLogout, onDeleteOrder,
    onAddCategory, onDeleteCategory
}) => {
    const [activeTab, setActiveTab] = useState("products");
    const [seenPendingOrderKeys, setSeenPendingOrderKeys] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("adminSeenPendingOrderKeys") || "[]");
        } catch {
            return [];
        }
    });
    const [orderSubTab, setOrderSubTab] = useState("all");   // "all" | "confirmed" | "placed"
    const [newCatName, setNewCatName] = useState("");
    const [orderSearch, setOrderSearch] = useState("");
    const [confirmedSearch, setConfirmedSearch] = useState("");
    const [placedSearch, setPlacedSearch] = useState("");
    const [sortNewest, setSortNewest] = useState(true);
    const [salesMonth, setSalesMonth] = useState(0);
    const [salesYear, setSalesYear] = useState(new Date().getFullYear());

    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

    const pendingOrders = useMemo(() =>
        orders.filter(o => (o.status || "pending").toLowerCase() === "pending"),
        [orders]
    );

    const pendingOrderKeys = useMemo(() =>
        pendingOrders.map(o => o.firebaseKey || o.orderId).filter(Boolean),
        [pendingOrders]
    );

    const newPendingCount = useMemo(() =>
        pendingOrderKeys.filter(key => !seenPendingOrderKeys.includes(key)).length,
        [pendingOrderKeys, seenPendingOrderKeys]
    );

    useEffect(() => {
        if (activeTab === "orders") {
            setSeenPendingOrderKeys(pendingOrderKeys);
            localStorage.setItem("adminSeenPendingOrderKeys", JSON.stringify(pendingOrderKeys));
        }
    }, [activeTab, pendingOrderKeys]);

    const confirmedOrders = useMemo(() =>
        orders.filter(o => (o.status || "").toLowerCase() === "confirmed"),
        [orders]
    );

    const placedOrders = useMemo(() =>
        orders.filter(o => (o.status || "").toLowerCase() === "placed"),
        [orders]
    );

    const invoiceOrders = useMemo(() =>
        orders.filter(o => ["confirmed", "placed"].includes((o.status || "").toLowerCase())),
        [orders]
    );

    /* ── Search confirmed orders by order ID or invoice ID ── */
    const searchedConfirmed = useMemo(() => {
        const q = confirmedSearch.trim().toLowerCase();
        if (!q) return confirmedOrders;
        return confirmedOrders.filter(o =>
            (o.orderId?.toLowerCase().includes(q)) ||
            (o.invoiceNumber?.toLowerCase().includes(q)) ||
            (o.name?.toLowerCase().includes(q))
        );
    }, [confirmedOrders, confirmedSearch]);

    const searchedPlaced = useMemo(() => {
        const q = placedSearch.trim().toLowerCase();
        if (!q) return placedOrders;
        return placedOrders.filter(o =>
            (o.orderId?.toLowerCase().includes(q)) ||
            (o.invoiceNumber?.toLowerCase().includes(q)) ||
            (o.name?.toLowerCase().includes(q))
        );
    }, [placedOrders, placedSearch]);

    /* ── Filter confirmed for sales report ── */
    const salesFiltered = useMemo(() => invoiceOrders.filter(o => {
        const { month, year } = parseOrderDate(o.date);
        return (salesMonth === 0 || month === salesMonth) && (salesYear === 0 || year === salesYear);
    }), [invoiceOrders, salesMonth, salesYear]);

    const salesRevenue = salesFiltered.reduce((s, o) => s + (Number(o.totalAmount) || 0), 0);

    /* ── Product breakdown for preview ── */
    const productBreakdown = useMemo(() => {
        const map = {};
        salesFiltered.forEach(o => {
            if (o.isBuyAll && o.items) {
                o.items.forEach(item => {
                    const k = item.productName || "Unknown";
                    if (!map[k]) map[k] = { name: k, qty: 0, amount: 0 };
                    map[k].qty++; map[k].amount += Number(item.price) || 0;
                });
            } else {
                const k = o.productName || "Unknown";
                const qty = Number(o.quantity) || 1;
                const amt = Number(o.productTotal) || Number(o.totalAmount) || 0;
                if (!map[k]) map[k] = { name: k, qty: 0, amount: 0 };
                map[k].qty += qty; map[k].amount += amt;
            }
        });
        return Object.values(map).sort((a, b) => b.amount - a.amount);
    }, [salesFiltered]);

    /* ── All orders search + sort ── */
    const filteredOrders = useMemo(() => pendingOrders
        .filter(o =>
            o.name?.toLowerCase().includes(orderSearch.toLowerCase()) ||
            o.orderId?.toLowerCase().includes(orderSearch.toLowerCase()) ||
            o.productName?.toLowerCase().includes(orderSearch.toLowerCase()) ||
            o.email?.toLowerCase().includes(orderSearch.toLowerCase()) ||
            o.phone?.includes(orderSearch) ||
            (o.items && o.items.some(i => i.productName?.toLowerCase().includes(orderSearch.toLowerCase())))
        )
        .sort((a, b) => {
            const da = new Date(a.date || 0), db2 = new Date(b.date || 0);
            return sortNewest ? db2 - da : da - db2;
        }),
        [pendingOrders, orderSearch, sortNewest]
    );

    /* ── Products ── */
    const filteredProducts = products.filter(p =>
        p.name.toLowerCase().includes(adminSearchQuery.toLowerCase())
    );
    const computedSP = newProdMrp && newProdDiscount
        ? Math.round(parseFloat(newProdMrp) - (parseFloat(newProdMrp) * parseFloat(newProdDiscount) / 100)) : 0;

    const handleImageUpload = (e) => {
        Array.from(e.target.files).forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => setNewProdImagesList(prev => [...prev, reader.result]);
            reader.readAsDataURL(file);
        });
    };

    const handleProductSubmit = (e) => {
        e.preventDefault();
        if (!newProdName || !newProdMrp || !newProdDiscount) { showToast("Please fill all required fields."); return; }
        const productData = {
            name: newProdName, description: newProdDescription, type: newProdType,
            mrp: parseFloat(newProdMrp), sellingPrice: computedSP,
            discount: parseFloat(newProdDiscount), images: newProdImagesList,
            availableColors: newProdSelectedColors, categoryId: newProdCategory,
            rating: 4.5, reviews: []
        };
        if (isEditMode && editProductId) {
            set(ref(db, `products/${editProductId}`), { ...productData, id: editProductId })
                .then(() => { showToast(`${newProdName} updated!`); cancelEditMode(); })
                .catch(() => showToast("Update failed."));
        } else {
            push(ref(db, "products"), productData)
                .then(() => { showToast(`${newProdName} added!`); cancelEditMode(); })
                .catch(() => showToast("Failed to add product."));
        }
    };

    const handleDeleteProduct = (id, name) => {
        if (!window.confirm(`Delete "${name}"?`)) return;
        remove(ref(db, `products/${id}`))
            .then(() => showToast(`${name} deleted.`))
            .catch(() => showToast("Delete failed."));
    };

    const toggleColor = (colorId) =>
        setNewProdSelectedColors(prev =>
            prev.includes(colorId) ? prev.filter(c => c !== colorId) : [...prev, colorId]
        );

    const handleSalesReport = () => {
        if (salesFiltered.length === 0) { showToast("No processed orders for selected period."); return; }
        generateSalesReportPDF(salesFiltered, salesMonth, salesYear);
    };

    /* ── Shared styles ── */
    const inputStyle = {
        height: "42px", padding: "0 14px",
        border: `1.5px solid ${myCustomTheme.colors.high_light}`,
        borderRadius: "10px", fontSize: "14px", outline: "none",
        backgroundColor: myCustomTheme.colors.header_footer_bg,
        color: myCustomTheme.colors.heading, width: "100%"
    };
    const statCard = (label, value, bg, col) => (
        <div style={{ background: bg, borderRadius: "14px", padding: "16px 18px", border: `1.5px solid ${myCustomTheme.colors.high_light}` }}>
            <p style={{ margin: "0 0 5px", fontSize: "10px", fontWeight: "700", color: myCustomTheme.colors.menu_text, textTransform: "uppercase", letterSpacing: ".7px" }}>{label}</p>
            <p style={{ margin: 0, fontWeight: "800", fontSize: "20px", color: col }}>{value}</p>
        </div>
    );

    const tabBtn = (key, label, Icon, count) => (
        <button key={key} onClick={() => setActiveTab(key)} style={{
            padding: "10px 20px", borderRadius: "12px", border: "none", cursor: "pointer",
            fontWeight: "700", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px",
            backgroundColor: activeTab === key ? myCustomTheme.colors.button_bg : myCustomTheme.colors.high_light,
            color: activeTab === key ? myCustomTheme.colors.button_text : myCustomTheme.colors.heading,
        }}>
            <Icon />{label}
            {count > 0 && <span style={{ background: "#ef4444", color: "#fff", borderRadius: "50%", width: "18px", height: "18px", fontSize: "10px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "800" }}>{count}</span>}
        </button>
    );

    const orderSubTabBtn = (key, label, Icon, count) => (
        <button onClick={() => setOrderSubTab(key)} style={{
            padding: "9px 18px", borderRadius: "10px", border: "none", cursor: "pointer",
            fontWeight: "700", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px",
            backgroundColor: orderSubTab === key ? myCustomTheme.colors.button_bg : myCustomTheme.colors.high_light,
            color: orderSubTab === key ? myCustomTheme.colors.button_text : myCustomTheme.colors.heading,
        }}>
            <Icon />{label}
        </button>
    );

    return (
        <div className="admin-dashboard-root" style={{ backgroundColor: myCustomTheme.colors.header_footer_bg, boxShadow: myCustomTheme.shadows.hard, borderRadius: "30px", border: `1px solid ${myCustomTheme.colors.high_light}` }}>

            {/* HEADER */}
            <div className="admin-dashboard-header">
                <div>
                    <h1 style={{ color: myCustomTheme.colors.heading }}>Admin Dashboard</h1>
                    <p className="welcome-tag">Welcome, {currentAdminName}</p>
                </div>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                    {tabBtn("products", "Products", FaBox, 0)}
                    {tabBtn("orders", "Orders", FaShoppingBag, newPendingCount)}
                    {tabBtn("invoice", "Invoice", FaFileInvoice, 0)}
                    {tabBtn("categories", "Categories", FaTag, 0)}
                    <button onClick={onLogout} style={{ padding: "10px 20px", borderRadius: "12px", border: "none", cursor: "pointer", fontWeight: "700", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px", backgroundColor: "#fee2e2", color: "#ef4444" }}>
                        <FaSignOutAlt />Logout
                    </button>
                </div>
            </div>

            {/* ══ PRODUCTS TAB ══ */}
            {activeTab === "products" && (
                <div className="admin-dashboard-layout">
                    <div className="admin-card-panel" style={{ backgroundColor: myCustomTheme.colors.bg_primary, borderColor: myCustomTheme.colors.high_light, borderRadius: "20px" }}>
                        <h2 style={{ color: myCustomTheme.colors.heading }}>{isEditMode ? <><FaEdit /> Edit Product</> : <><FaPlusCircle /> Add New Product</>}</h2>
                        <form onSubmit={handleProductSubmit} className="checkout-form">
                            <div className="form-group"><label className="input-label">Product Name *</label>
                                <input type="text" placeholder="e.g. Photo Frame" value={newProdName} onChange={e => setNewProdName(e.target.value)} required /></div>
                            <div className="form-group"><label className="input-label">Type *</label>
                                <div className="radio-group-flex">{["Non-Customize", "Customize"].map(t => (
                                    <label key={t} className="radio-container"><input type="radio" name="prodType" value={t} checked={newProdType === t} onChange={() => setNewProdType(t)} />{t}</label>
                                ))}</div></div>
                            <div className="form-row-twin">
                                <div className="form-group"><label className="input-label">MRP (Rs.) *</label>
                                    <input type="number" placeholder="e.g. 800" value={newProdMrp} onChange={e => setNewProdMrp(e.target.value)} min="0" required /></div>
                                <div className="form-group"><label className="input-label">Discount (%) *</label>
                                    <input type="number" placeholder="e.g. 20" value={newProdDiscount} onChange={e => setNewProdDiscount(e.target.value)} min="0" max="90" required /></div>
                            </div>
                            {computedSP > 0 && <div style={{ padding: "12px 16px", backgroundColor: myCustomTheme.colors.high_light, borderRadius: "10px", fontSize: "14px", fontWeight: "700", color: myCustomTheme.colors.heading }}>Selling Price: Rs.{computedSP}</div>}
                            <div className="form-group"><label className="input-label">Category</label>
                                <select value={newProdCategory} onChange={e => setNewProdCategory(e.target.value)}>
                                    <option value="">-- No Category --</option>
                                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select></div>
                            <div className="form-group"><label className="input-label">Description</label>
                                <textarea placeholder="Product description..." value={newProdDescription} onChange={e => setNewProdDescription(e.target.value)} /></div>
                            <div className="form-group"><label className="input-label">Available Colors</label>
                                <div className="color-checkbox-grid">{MASTER_COLOR_PALETTE.map(c => (
                                    <label key={c.id} className="checkbox-pill-container">
                                        <input type="checkbox" checked={newProdSelectedColors.includes(c.id)} onChange={() => toggleColor(c.id)} />
                                        <span style={{ width: "14px", height: "14px", borderRadius: "50%", backgroundColor: c.hex, display: "inline-block", border: "1.5px solid #e2e8f0", flexShrink: 0 }} />{c.name}
                                    </label>
                                ))}</div></div>
                            <div className="form-group"><label className="input-label">Product Images</label>
                                <div className="file-upload-dropzone">
                                    <input type="file" id="prod-img-upload" accept="image/*" multiple onChange={handleImageUpload} style={{ display: "none" }} />
                                    <label htmlFor="prod-img-upload" className="file-upload-label"><FaUpload />Upload Images</label>
                                </div>
                                {newProdImagesList.length > 0 && (
                                    <div className="image-previews-tray" style={{ marginTop: "10px" }}>
                                        {newProdImagesList.map((src, i) => (
                                            <div key={i} className="preview-thumb-wrapper">
                                                <img src={src} alt="" className="admin-form-thumb" />
                                                <button type="button" className="thumb-remove-btn" onClick={() => setNewProdImagesList(p => p.filter((_, j) => j !== i))}>×</button>
                                            </div>
                                        ))}
                                    </div>
                                )}</div>
                            <div style={{ display: "flex", gap: "12px" }}>
                                <button type="submit" className="continue-btn" style={{ flex: 1, width: "0% !important" }}>{isEditMode ? "Save Changes" : "Add Product"}</button>
                                {isEditMode && <button type="button" className="continue-btn" onClick={cancelEditMode} style={{ flex: "0 0 auto", background: "#f1f5f9", color: "#475569", padding: "0 20px" }}>Cancel</button>}
                            </div>
                        </form>
                    </div>

                    <div className="admin-card-panel" style={{ backgroundColor: myCustomTheme.colors.bg_primary, borderColor: myCustomTheme.colors.high_light, borderRadius: "20px" }}>
                        <h2 style={{ color: myCustomTheme.colors.heading }}><FaBox />Product Registry ({filteredProducts.length})</h2>
                        <div style={{ position: "relative", marginBottom: "18px" }}>
                            <FaSearch style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: myCustomTheme.colors.menu_text }} />
                            <input type="text" placeholder="Search products..." value={adminSearchQuery} onChange={e => setAdminSearchQuery(e.target.value)} style={{ ...inputStyle, paddingLeft: "40px" }} />
                        </div>
                        <div className="admin-registry-scroll">
                            {filteredProducts.map(p => (
                                <div key={p.id} className={`registry-row-item${isEditMode && editProductId === p.id ? " highlight-edit-row" : ""}`} style={{ backgroundColor: myCustomTheme.colors.header_footer_bg, borderColor: myCustomTheme.colors.high_light, borderRadius: "14px" }}>
                                    <img src={p.images && p.images[0]} alt="" className="registry-item-thumb" style={{ borderRadius: "10px" }} />
                                    <div className="registry-item-details">
                                        <p className="registry-item-name" style={{ color: myCustomTheme.colors.heading }}>{p.name}</p>
                                        <p className="registry-item-meta" style={{ color: myCustomTheme.colors.menu_text, fontSize: "12px" }}>MRP: Rs.{p.mrp} | SP: Rs.{p.sellingPrice} | {p.discount}% off</p>
                                        <span style={{ display: "inline-block", marginTop: "4px", padding: "2px 9px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", background: p.type === "Customize" ? "#fef3c7" : "#eff6ff", color: p.type === "Customize" ? "#92400e" : "#1d4ed8" }}>{p.type}</span>
                                    </div>
                                    <div style={{ display: "flex", gap: "8px" }}>
                                        <button className="registry-edit-btn" onClick={() => startEditProductWorkflow(p)} title="Edit"><FaEdit /></button>
                                        <button className="registry-delete-btn" onClick={() => handleDeleteProduct(p.id, p.name)} title="Delete"><FaTrashAlt /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ══ ORDERS TAB ══ */}
            {activeTab === "orders" && (
                <div style={{ padding: "28px" }}>

                    {/* Stats */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(155px,1fr))", gap: "12px", marginBottom: "22px" }}>
                        {statCard("Total Orders", orders.length, myCustomTheme.colors.bg_primary, myCustomTheme.colors.heading)}
                        {statCard("Total Revenue", `Rs.${orders.reduce((s, o) => s + (Number(o.totalAmount) || 0), 0)}`, "#f0fdf4", "#15803d")}
                        {statCard("Pending", pendingOrders.length, "#fffbeb", "#92400e")}
                        {statCard("Confirmed", confirmedOrders.length, "#dcfce7", "#15803d")}
                        {statCard("Placed", placedOrders.length, "#dbeafe", "#1d4ed8")}
                        {statCard("Cart Orders", orders.filter(o => o.isBuyAll).length, "#eff6ff", "#1d4ed8")}
                        {statCard("Single Orders", orders.filter(o => !o.isBuyAll).length, myCustomTheme.colors.bg_primary, myCustomTheme.colors.heading)}
                    </div>

                    {/* Sub-tab selector */}
                    <div style={{ display: "flex", gap: "10px", marginBottom: "22px", flexWrap: "wrap" }}>
                        {orderSubTabBtn("all", "All Orders", FaListAlt, pendingOrders.length)}
                        {orderSubTabBtn("confirmed", "Confirmed Orders", FaClipboardCheck, confirmedOrders.length)}
                        {orderSubTabBtn("placed", "Placed Orders", FaTruck, placedOrders.length)}
                    </div>

                    {/* ── ALL ORDERS sub-tab ── */}
                    {orderSubTab === "all" && (
                        <div>
                            <div style={{ display: "flex", gap: "12px", marginBottom: "18px", flexWrap: "wrap" }}>
                                <div style={{ flex: 1, minWidth: "200px", position: "relative" }}>
                                    <FaSearch style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: myCustomTheme.colors.menu_text, fontSize: "13px" }} />
                                    <input type="text" placeholder="Search by name, order ID, email, phone, product..." value={orderSearch} onChange={e => setOrderSearch(e.target.value)} style={{ ...inputStyle, paddingLeft: "40px" }} />
                                </div>
                                <button onClick={() => setSortNewest(!sortNewest)} style={{ padding: "0 18px", height: "42px", borderRadius: "10px", border: "none", backgroundColor: myCustomTheme.colors.high_light, color: myCustomTheme.colors.heading, fontWeight: "700", fontSize: "13px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
                                    <FaSortAmountDown />{sortNewest ? "Newest First" : "Oldest First"}
                                </button>
                            </div>
                            {filteredOrders.length === 0
                                ? <div style={{ textAlign: "center", padding: "50px", color: myCustomTheme.colors.menu_text }}><FaShoppingBag style={{ fontSize: "48px", marginBottom: "16px", opacity: .3 }} /><p style={{ fontSize: "16px", fontWeight: "600" }}>{orderSearch ? "No orders match." : "No pending orders yet."}</p></div>
                                : filteredOrders.map(o => <OrderCard key={o.firebaseKey} order={o} onDelete={onDeleteOrder} theme={myCustomTheme} actionMode="pending" />)
                            }
                        </div>
                    )}

                    {/* ── CONFIRMED ORDERS sub-tab ── */}
                    {orderSubTab === "confirmed" && (
                        <div>
                            {/* Search by order ID / invoice ID */}
                            <div style={{ position: "relative", marginBottom: "18px" }}>
                                <FaSearch style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: myCustomTheme.colors.menu_text, fontSize: "13px" }} />
                                <input
                                    type="text"
                                    placeholder="Search by Order ID, Invoice ID, or Customer Name..."
                                    value={confirmedSearch}
                                    onChange={e => setConfirmedSearch(e.target.value)}
                                    style={{ ...inputStyle, paddingLeft: "40px" }}
                                />
                            </div>

                            {/* Confirmed order cards */}
                            {searchedConfirmed.length === 0
                                ? <div style={{ textAlign: "center", padding: "50px", color: myCustomTheme.colors.menu_text }}>
                                    <FaClipboardCheck style={{ fontSize: "48px", marginBottom: "16px", opacity: .3 }} />
                                    <p style={{ fontSize: "16px", fontWeight: "600" }}>{confirmedSearch ? "No orders match your search." : "No confirmed orders yet. Confirm pending orders from All Orders tab."}</p>
                                </div>
                                : searchedConfirmed.map(o => <OrderCard key={o.firebaseKey} order={o} onDelete={onDeleteOrder} theme={myCustomTheme} actionMode="confirmed" />)
                            }
                        </div>
                    )}

                    {/* ── PLACED ORDERS sub-tab ── */}
                    {orderSubTab === "placed" && (
                        <div>
                            <div style={{ position: "relative", marginBottom: "18px" }}>
                                <FaSearch style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: myCustomTheme.colors.menu_text, fontSize: "13px" }} />
                                <input
                                    type="text"
                                    placeholder="Search placed orders by Order ID, Invoice ID, or Customer Name..."
                                    value={placedSearch}
                                    onChange={e => setPlacedSearch(e.target.value)}
                                    style={{ ...inputStyle, paddingLeft: "40px" }}
                                />
                            </div>

                            {searchedPlaced.length === 0
                                ? <div style={{ textAlign: "center", padding: "50px", color: myCustomTheme.colors.menu_text }}>
                                    <FaTruck style={{ fontSize: "48px", marginBottom: "16px", opacity: .3 }} />
                                    <p style={{ fontSize: "16px", fontWeight: "600" }}>{placedSearch ? "No placed orders match your search." : "No placed orders yet."}</p>
                                </div>
                                : searchedPlaced.map(o => <OrderCard key={o.firebaseKey} order={o} onDelete={onDeleteOrder} theme={myCustomTheme} actionMode="placed" />)
                            }
                        </div>
                    )}

                </div>
            )}

            {/* ══ INVOICE TAB ══ */}
            {activeTab === "invoice" && (
                <div style={{ padding: "28px" }}>
                    {/* Monthly / Yearly Sales Invoice Panel */}
                    <div style={{ background: myCustomTheme.colors.bg_primary, borderRadius: "18px", border: `1.5px solid ${myCustomTheme.colors.high_light}`, overflow: "hidden", marginBottom: "22px" }}>
                        {/* Panel header */}
                        <div style={{ padding: "16px 20px", background: "#0f172a", display: "flex", alignItems: "center", gap: "10px" }}>
                            <FaChartBar style={{ color: "#7dd3fc", fontSize: "16px" }} />
                            <span style={{ fontWeight: "700", fontSize: "15px", color: "#f1f5f9" }}>Monthly / Yearly Sales Invoice</span>
                            <span style={{ marginLeft: "auto", fontSize: "12px", color: "#94a3b8" }}>Sales report for processed orders</span>
                        </div>

                        <div style={{ padding: "20px" }}>
                            {/* Filter row */}
                            <div style={{ display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
                                <div style={{ flex: 1, minWidth: "160px" }}>
                                    <label style={{ fontSize: "11px", fontWeight: "700", color: myCustomTheme.colors.menu_text, textTransform: "uppercase", letterSpacing: ".6px", display: "block", marginBottom: "6px" }}>
                                        <FaFilter style={{ marginRight: "4px" }} />Month
                                    </label>
                                    <select value={salesMonth} onChange={e => setSalesMonth(Number(e.target.value))} style={inputStyle}>
                                        {MONTH_NAMES.map((m, i) => <option key={i} value={i}>{m}</option>)}
                                    </select>
                                </div>
                                <div style={{ flex: 1, minWidth: "120px" }}>
                                    <label style={{ fontSize: "11px", fontWeight: "700", color: myCustomTheme.colors.menu_text, textTransform: "uppercase", letterSpacing: ".6px", display: "block", marginBottom: "6px" }}>
                                        <FaCalendarAlt style={{ marginRight: "4px" }} />Year
                                    </label>
                                    <select value={salesYear} onChange={e => setSalesYear(Number(e.target.value))} style={inputStyle}>
                                        <option value={0}>All Years</option>
                                        {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Product breakdown preview table */}
                            {productBreakdown.length > 0 && (
                                <div style={{ marginBottom: "16px", border: `1.5px solid ${myCustomTheme.colors.high_light}`, borderRadius: "12px", overflow: "hidden" }}>
                                    <div style={{ display: "grid", gridTemplateColumns: "32px 1fr auto auto", gap: "8px", padding: "10px 14px", background: "#1e293b" }}>
                                        <span style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8" }}>#</span>
                                        <span style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8" }}>PRODUCT NAME</span>
                                        <span style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8", textAlign: "center" }}>QTY SOLD</span>
                                        <span style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8", textAlign: "right" }}>TOTAL AMOUNT</span>
                                    </div>
                                    {productBreakdown.map((p, idx) => (
                                        <div key={idx} style={{ display: "grid", gridTemplateColumns: "32px 1fr auto auto", gap: "8px", padding: "11px 14px", alignItems: "center", background: idx % 2 === 0 ? myCustomTheme.colors.header_footer_bg : myCustomTheme.colors.bg_primary, borderTop: `1px solid ${myCustomTheme.colors.high_light}` }}>
                                            <span style={{ width: "22px", height: "22px", background: myCustomTheme.colors.button_bg, color: myCustomTheme.colors.button_text, borderRadius: "50%", fontSize: "10px", fontWeight: "800", display: "flex", alignItems: "center", justifyContent: "center" }}>{idx + 1}</span>
                                            <span style={{ fontSize: "13px", fontWeight: "600", color: myCustomTheme.colors.heading }}>{p.name}</span>
                                            <span style={{ fontSize: "13px", fontWeight: "700", color: myCustomTheme.colors.heading, textAlign: "center" }}>{p.qty}</span>
                                            <span style={{ fontSize: "14px", fontWeight: "800", color: "#16a34a", textAlign: "right" }}>Rs.{p.amount.toLocaleString("en-IN")}</span>
                                        </div>
                                    ))}
                                    {/* Totals row */}
                                    <div style={{ display: "grid", gridTemplateColumns: "32px 1fr auto auto", gap: "8px", padding: "12px 14px", alignItems: "center", background: "#f1f5f9", borderTop: `2px solid ${myCustomTheme.colors.high_light}` }}>
                                        <span />
                                        <span style={{ fontSize: "13px", fontWeight: "800", color: myCustomTheme.colors.heading }}>Total — {salesFiltered.length} orders</span>
                                        <span style={{ fontSize: "13px", fontWeight: "800", color: myCustomTheme.colors.heading, textAlign: "center" }}>
                                            {productBreakdown.reduce((s, p) => s + p.qty, 0)}
                                        </span>
                                        <span style={{ fontSize: "15px", fontWeight: "800", color: "#16a34a", textAlign: "right" }}>Rs.{salesRevenue.toLocaleString("en-IN")}</span>
                                    </div>
                                </div>
                            )}

                            {salesFiltered.length === 0 && (
                                <div style={{ textAlign: "center", padding: "20px", color: myCustomTheme.colors.menu_text }}>
                                    <p style={{ fontSize: "14px" }}>No processed orders for selected period.</p>
                                </div>
                            )}

                            {/* Generate button */}
                            <button
                                onClick={handleSalesReport}
                                style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "none", background: "#0f172a", color: "#fff", fontWeight: "700", fontSize: "15px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}
                            >
                                <FaFileAlt />
                                Generate &amp; Download Sales Invoice PDF
                                <span style={{ fontSize: "12px", fontWeight: "500", color: "#94a3b8" }}>
                                    ({salesMonth > 0 ? MONTH_NAMES[salesMonth] : "All Months"}{salesYear > 0 ? ` ${salesYear}` : ""} &mdash; {salesFiltered.length} orders)
                                </span>
                            </button>
                        </div>
                    </div>

                </div>
            )}


            {/* ══ CATEGORIES TAB ══ */}
            {activeTab === "categories" && (
                <div className="admin-card-panel" style={{ backgroundColor: myCustomTheme.colors.bg_primary, borderColor: myCustomTheme.colors.high_light, borderRadius: "20px", maxWidth: "540px" }}>
                    <h2 style={{ color: myCustomTheme.colors.heading }}><FaTag />Categories</h2>
                    <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
                        <input type="text" placeholder="New category name..." value={newCatName} onChange={e => setNewCatName(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                        <button onClick={() => { if (newCatName.trim()) { onAddCategory(newCatName.trim()); setNewCatName(""); } }} style={{ padding: "0 20px", height: "42px", borderRadius: "10px", border: "none", backgroundColor: myCustomTheme.colors.button_bg, color: myCustomTheme.colors.button_text, fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
                            <FaPlusCircle />Add
                        </button>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        {categories.length === 0
                            ? <p style={{ color: myCustomTheme.colors.menu_text, textAlign: "center", padding: "20px" }}>No categories yet.</p>
                            : categories.map(c => (
                                <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", backgroundColor: myCustomTheme.colors.header_footer_bg, borderRadius: "10px", border: `1px solid ${myCustomTheme.colors.high_light}` }}>
                                    <span style={{ fontWeight: "600", color: myCustomTheme.colors.heading }}><FaTag style={{ marginRight: "8px", color: myCustomTheme.colors.icon_color }} />{c.name}</span>
                                    <button className="registry-delete-btn" onClick={() => onDeleteCategory(c.id)} title="Delete"><FaTrashAlt /></button>
                                </div>
                            ))
                        }
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;